import { useId, useMemo, useState, type MouseEvent } from 'react';
import { OTHER_COLOUR, SLICE_COLOURS, innerWallPath, labelPoint, outerWallPath, shade, sliceAngles, topPath, type DonutGeometry } from './donut3d';

export interface DonutSlice {
  label: string;
  /** Language of the label when it differs from the chart (WCAG 3.1.2). */
  lang?: string;
  value: number;
  /** Formatted value and share, e.g. "12" and "24 %". */
  valueText: string;
  shareText: string;
  /** The "Ostatné" bucket (neutral grey). */
  other?: boolean;
}

export function sliceColour(index: number, slice: { other?: boolean }): string {
  return slice.other ? OTHER_COLOUR : SLICE_COLOURS[index % SLICE_COLOURS.length]!;
}

const GEOMETRY: DonutGeometry = { cx: 190, cy: 104, rx: 168, ry: 76, inner: 0.52, depth: 30 };
const WIDTH = 380;
const HEIGHT = 222;
/** Slices from this share up get their percentage written on the ring. */
const LABEL_MIN_SHARE = 0.08;

/**
 * Tilted 3D donut in SVG. Slice angles are exact; the tilt is only the projection, so the data table next to the
 * chart (rendered by the caller) carries the exact values – the SVG itself is one image with a text alternative.
 * Hovering a slice (or the matching table row, through {@code active}) emphasises it and shows a tooltip.
 */
export function Donut3D({
  slices,
  title,
  description,
  totalText,
  active,
  onActiveChange,
}: {
  slices: DonutSlice[];
  title: string;
  description: string;
  totalText: string;
  active: number | null;
  onActiveChange: (index: number | null) => void;
}) {
  const titleId = useId();
  const descId = useId();
  const angles = useMemo(() => sliceAngles(slices.map((s) => s.value)), [slices]);
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  const hover = (index: number) => ({
    onMouseEnter: () => onActiveChange(index),
    onMouseMove: (e: MouseEvent<SVGElement>) => {
      const box = (e.currentTarget.ownerSVGElement ?? (e.currentTarget as unknown as SVGSVGElement)).getBoundingClientRect();
      setPointer({ x: e.clientX - box.left, y: e.clientY - box.top });
    },
    onMouseLeave: () => {
      onActiveChange(null);
      setPointer(null);
    },
  });
  const dim = (index: number) => (active !== null && active !== index ? 0.35 : 1);
  const slice = active !== null ? slices[active] : undefined;

  return (
    <div className="app-donut">
      <svg
        className="app-donut__svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-labelledby={titleId}
        aria-describedby={descId}
        focusable="false"
      >
        <title id={titleId}>{title}</title>
        <desc id={descId}>{description}</desc>
        {/* soft shadow under the donut */}
        <ellipse cx={GEOMETRY.cx} cy={GEOMETRY.cy + GEOMETRY.depth + 6} rx={GEOMETRY.rx * 0.96} ry={GEOMETRY.ry * 0.62} className="app-donut__shadow" />
        <g className="app-donut__walls-inner">
          {angles.map((a, i) => {
            const d = innerWallPath(GEOMETRY, a);
            return d ? <path key={i} d={d} fill={shade(sliceColour(i, slices[i]!), 0.42)} opacity={dim(i)} {...hover(i)} /> : null;
          })}
        </g>
        <g className="app-donut__tops">
          {angles.map((a, i) => {
            const d = topPath(GEOMETRY, a);
            return d ? <path key={i} d={d} fill={sliceColour(i, slices[i]!)} opacity={dim(i)} {...hover(i)} /> : null;
          })}
        </g>
        <g className="app-donut__walls-outer">
          {angles.map((a, i) => {
            const d = outerWallPath(GEOMETRY, a);
            return d ? <path key={i} d={d} fill={shade(sliceColour(i, slices[i]!), 0.24)} opacity={dim(i)} {...hover(i)} /> : null;
          })}
        </g>
        <g className="app-donut__labels" aria-hidden="true">
          {angles.map((a, i) => {
            if (total <= 0 || slices[i]!.value / total < LABEL_MIN_SHARE) return null;
            const p = labelPoint(GEOMETRY, a);
            return (
              <text key={i} x={p.x} y={p.y} className="app-donut__label" opacity={dim(i)}>
                {slices[i]!.shareText}
              </text>
            );
          })}
          <text x={GEOMETRY.cx} y={GEOMETRY.cy + GEOMETRY.depth / 2 + 4} className="app-donut__total">
            {totalText}
          </text>
        </g>
      </svg>
      {slice && pointer && (
        <div className="app-donut__tooltip" style={{ left: pointer.x, top: pointer.y }} aria-hidden="true">
          <span className="app-donut__swatch" style={{ background: sliceColour(active!, slice) }} />
          <strong lang={slice.lang}>{slice.label}</strong>
          <br />
          {slice.valueText} ({slice.shareText})
        </div>
      )}
    </div>
  );
}
